<template>
	<Dialog>
		<DialogContent :disable-overlay-close="disableOverlayClose" class="gap-1" @close="$emit('close')">
			<DialogTitle>Record Audio</DialogTitle>
			<DialogDescription> Record audio using your microphone and upload it to your files. </DialogDescription>
			<div
				v-if="!audioPermissionGranted"
				class="mx-auto flex w-full flex-col gap-2 text-center text-lg text-blue-500"
			>
				<p class="py-4">Please grant microphone access to record audio.</p>
				<button class="jmiBtn blue" @click="checkAudioPermission">Request Permission</button>
			</div>
			<div
				v-else-if="!mediaSupported"
				class="mx-auto flex w-full flex-col gap-2 text-center text-lg text-blue-500"
			>
				<p class="py-4">
					Recording is unsupported on this browser type. Please use a supported browser such as Chrome,
					Firefox or Safari.
				</p>
				<button class="jmiBtn red" @click="$emit('close')">Close</button>
			</div>
			<div v-else>
				<div
					v-if="(tenants?.length > 1 || $global.hasUserRole(userRoles, 'reseller')) && !hideTenantSelector"
					class="mx-auto w-full"
				>
					<div class="formGroup withIcon">
						<label class="textGrey">Tenant</label>
						<select v-model="selectedTenant" @change="selectedAccount = null">
							<option disabled :value="null">-- Select Tenant --</option>
							<option v-for="t in tenants" :key="t.id" :value="t.id">
								{{ t.companyName }}
							</option>
						</select>
						<span class="fa-light fa-chevron-down"></span>
					</div>
				</div>
				<div v-if="selectedTenant && !hideAccountSelector" class="mx-auto w-full">
					<div class="formGroup withIcon">
						<label class="textGrey">Account</label>
						<select v-model="selectedAccount">
							<option :value="null">-- Unassigned - Tenant File --</option>
							<option v-for="a in tenantAccounts" :key="a.id" :value="a.id">
								{{ a.name }}
							</option>
						</select>
						<span class="fa-light fa-chevron-down"></span>
					</div>
				</div>
				<div v-if="audioPermissionGranted" class="formGroup withIcon">
					<label class="textGrey">Audio Input Device</label>
					<select v-model="selectedDeviceId" :disabled="!audioInputDevices?.length">
						<option v-if="!audioInputDevices?.length" disabled :value="null">
							Loading audio devices, please wait...
						</option>
						<option v-for="device in audioInputDevices" :key="device.deviceId" :value="device.deviceId">
							{{ device.label }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
				<div class="formGroup pb-4">
					<label class="textGrey">Recording Name</label>
					<input v-model="recordingName" :placeholder="defaultRecordingName" type="text" />
				</div>
				<div class="formGroup flex gap-2">
					<button
						:disabled="isRecording || !audioPermissionGranted || !selectedDeviceId || !selectedTenant"
						class="jmiBtn green"
						@click="startRecording"
					>
						Start
					</button>
					<button :disabled="!isRecording" class="jmiBtn red" @click="stopRecording">Stop</button>
					<button :disabled="!recordedBlob" class="jmiBtn yellow" @click="removeRecording">Remove</button>
				</div>

				<div v-if="recordedBlob" class="w-full">
					<div class="overflow-hidden rounded-md p-4 pb-0">
						<media-player :file="recordedBlob" />
					</div>
					<div class="formGroup">
						<button :disabled="!selectedTenant || !recordedBlob" class="jmiBtn green" @click="uploadFile">
							Upload
						</button>
					</div>
				</div>
			</div>
		</DialogContent>
	</Dialog>
</template>

<script>
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import accountsApi from '@/scripts/accounts/accounts.api'
import filesApi from '@/scripts/files/files.api'
import moment from 'moment'

export default {
	components: {
		Dialog,
		DialogContent,
		DialogTitle,
		DialogDescription
	},
	inject: ['user', 'userRoles', 'tenants'],
	props: ['disableOverlayClose', 'selectedTenantId', 'selectedAccountId', 'loadedAccounts'],
	emits: ['close'],
	data() {
		return {
			accounts: [],
			selectedTenant: null,
			selectedAccount: null,
			hideTenantSelector: false,
			hideAccountSelector: false,
			isRecording: false,
			recordedBlob: null,
			mediaRecorder: null,
			audioChunks: [],
			audioInputDevices: [],
			selectedDeviceId: null,
			recordingName: '',
			defaultRecordingName: `My Recording - ${moment().format('YYYYMMDD_HHmmss')}`,
			audioPermissionGranted: false,
			stream: null,
			mimeType: '',
			mediaSupported: true
		}
	},
	computed: {
		tenantAccounts() {
			return this.accounts
				.filter((a) => a.tenant.id === this.selectedTenant)
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		fileExt() {
			return this.mimeType === 'audio/webm;codecs=opus' ? 'opus' : this.mimeType === 'audio/mp4' ? 'm4a' : 'wav'
		}
	},
	mounted() {
		if (this.selectedTenantId) {
			this.selectedTenant = this.selectedTenantId
			this.hideTenantSelector = true
		} else if (this.tenants?.length === 1) {
			this.selectedTenant = this.tenants[0].id
			this.hideTenantSelector = true
		}
		if (this.loadedAccounts) {
			this.accounts = this.loadedAccounts
		} else {
			accountsApi
				.getAll({ limit: 2500 })
				.then((result) => {
					this.accounts = result.data.results

					if (this.selectedAccountId && this.selectedTenantId) {
						this.selectedAccount = this.selectedAccountId
						this.hideAccountSelector = true
					}
				})
				.catch(console.error)
		}
		this.checkAudioPermission()
	},
	methods: {
		startRecording() {
			navigator.mediaDevices
				.getUserMedia({
					audio: { deviceId: this.selectedDeviceId ? { exact: this.selectedDeviceId } : undefined }
				})
				.then((stream) => {
					this.stream = stream
					if (MediaRecorder.isTypeSupported('audio/webm;codecs=pcm')) {
						this.mimeType = 'audio/webm;codecs=pcm'
					} else if (MediaRecorder.isTypeSupported('audio/mp4')) {
						this.mimeType = 'audio/mp4'
					} else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
						this.mimeType = 'audio/webm;codecs=opus'
					} else {
						this.mediaSupported = false
						return
					}
					this.mediaRecorder = new MediaRecorder(stream, { mimeType: this.mimeType })
					this.mediaRecorder.start()
					this.isRecording = true
					this.audioChunks = []

					this.mediaRecorder.ondataavailable = (event) => {
						this.audioChunks.push(event.data)
					}

					this.mediaRecorder.onstop = () => {
						this.recordedBlob = new Blob(this.audioChunks, { type: this.mimeType })
						this.stream.getTracks().forEach((track) => track.stop())
					}
				})
				.catch((error) => {
					console.error(error)
					alert('Please grant microphone access to record audio.')
				})
		},
		stopRecording() {
			if (this.mediaRecorder) {
				this.mediaRecorder.stop()
				this.isRecording = false
			}
		},
		removeRecording() {
			this.recordedBlob = null
			this.audioChunks = []
		},
		uploadFile() {
			let formData = new FormData()
			const fileName = this.recordingName
				? `${this.recordingName}.${this.fileExt}`
				: `${this.defaultRecordingName}.${this.fileExt}`
			formData.append('file', this.recordedBlob, fileName)

			filesApi[this.selectedAccount ? 'createAccountFile' : 'createTenantFile'](
				this.selectedAccount || this.selectedTenant,
				formData
			)
				.then((result) => {
					console.log(result)
					this.selectedTenant = null
					this.selectedAccount = null
					this.recordedBlob = null
					this.recordingName = ''
					this.$emit('close', result)
				})
				.catch(console.error)
		},
		async loadAudioInputDevices() {
			navigator.mediaDevices
				.enumerateDevices()
				.then((devices) => {
					this.audioInputDevices = devices.filter((device) => device.kind === 'audioinput')
					if (this.audioInputDevices.length > 0) this.selectedDeviceId = this.audioInputDevices[0].deviceId
				})
				.catch(console.error)
		},
		checkAudioPermission() {
			navigator.mediaDevices
				.getUserMedia({ audio: true })
				.then(async (stream) => {
					this.audioPermissionGranted = true
					try {
						await this.loadAudioInputDevices()
					} catch (error) {
						console.error('loading audio input devices', error)
					}
					stream.getTracks().forEach((track) => track.stop())
				})
				.catch((error) => {
					this.audioPermissionGranted = false
				})
		}
	}
}
</script>
